import React, { useState, useEffect } from "react";
import moment from "moment";
import Parse from "parse";
import useDebounce from "../../hooks";
import { useSelector } from "react-redux";
import { getCurrentUser } from "../../reducers/currentUser";
import { getLists } from "../../reducers/lists";
import HandleDiscountForm from "../../containers/form/HandleDiscountForm";
import HorizontalLinearStepper from "../HorizontalLinearStepper";
import {
  makeStyles,
  Paper,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Snackbar,
  AppBar,
  Toolbar,
  IconButton,
  Tooltip,
} from "@material-ui/core";
import DataGridView from "../reactgrid/DataGridView";
import { setNumberColumns, filtersQuery, translateColumn } from "../../utils2";
import DeleteIcon from "@material-ui/icons/Delete";
import EditIcon from "@material-ui/icons/Edit";
import CloseIcon from "@material-ui/icons/Close";

const useStyles = makeStyles(() => ({
  tableTopWrapper: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "",
  },
  tableTitleWrapper: {
    display: "flex",
    alignItems: "center",
    minHeight: "64px",
    padding: "0 8px 0 24px",
  },
  addBtnWrapper: {
    display: "flex",
    alignItems: "center",
    padding: "0 24px",
  },
  toolbar: {
    backgroundColor: "#46494c", //'#02cfa3'
  },
  noDiscount: {
    textAlign: "center",
    margin: "10rem 0 0",
  },
  expiré: {
    background:
      "repeating-linear-gradient(135deg, #fff0f0, #fff0f0 8px, #fff6f6 8px, #fff6f6 16px)",
    color: "#fff",
  },
}));
const columns = [
  { name: "includedClients", title: "Magasin(s) concerné(s)" },
  { name: "includedGroups", title: "Groupe(s) concerné(s)" },
  { name: "includedTradeNames", title: "Enseigne(s) concernée(s)" },
  { name: "excludedClients", title: "Magasin(s) exclu(s)" },
  { name: "excludedGroups", title: "Groupe(s) exclu(s)" },
  { name: "excludedTradeNames", title: "Enseigne(s) exclue(s)" },
  { name: "startDate", title: "Début", type: "date" },
  { name: "endDate", title: "Fin", type: "date" },
  { name: "percentageDiscount", title: "Remise", type: "numeric" },
  { name: "updatedAt", title: "Dernière modif.", type: "date" },
  { name: "lastUpdater", title: "Modifié par", type: "string" },
];
const columnExtensions = [
  { columnName: "includedClients", wordWrapEnabled: true },
  { columnName: "includedGroups", wordWrapEnabled: true },
  { columnName: "includedTradeNames", wordWrapEnabled: true },
  { columnName: "excludedClients", wordWrapEnabled: true },
  { columnName: "excludedGroups", width: 400, wordWrapEnabled: true },
  { columnName: "excludedTradeNames", wordWrapEnabled: true },
  { columnName: "startDate", width: 125, align: "right" },
  { columnName: "endDate", width: 125, align: "right" },
  { columnName: "percentageDiscount", width: 70, align: "right" },
  { columnName: "updatedAt" },
  { columnName: "lastUpdater" },
];
const pageSizes = [15, 25, 50, 100, 150];

const ProductDiscountList = (props) => {
  const { editable, addable } = props;
  const classes = useStyles();
  const currentUser = useSelector(getCurrentUser);
  const listsOptions = useSelector(getLists);
  const [numberColumns, setStateNumberColumns] = useState([]);
  const [filters, setFilters] = useState([]);
  const debouncedFilters = useDebounce(filters, 500);
  const [sorting, setSorting] = useState([
    { columnName: "percentageDiscount", direction: "asc" },
  ]);
  const [totalCount, setTotalCount] = useState(100);
  const [pageSize, setPageSize] = useState(25);
  const [currentPage, setCurrentPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [discounts, setDiscounts] = useState(undefined);
  const [snackBarOpen, setSnackBarOpen] = useState(false);
  const [snackBarText, setSnackBarText] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogTitle, setDialogTitle] = useState(null);
  const [dialogText, setDialogText] = useState(null);
  const [dialogYesBtnText, setDialogYesBtnText] = useState(null);
  const [dialogNoBtnText, setDialogNoBtnText] = useState(null);
  const [dialogOperation, setDialogOperation] = useState(null);
  const [discountId, setDiscountId] = useState(null);
  const [columnsTranslations, setColumnsTranslations] = useState([]);

  /********************************************************/
  /************ content info for paging panel**************/
  /********************************************************/
  const contentInfo = ({ from, to, count }) => {
    return `${from} ${from < to ? ` à ${to}` : ""} sur ${count} remise(s)`;
  };

  /********************************************************/
  /*********************** Navigation *********************/
  /********************************************************/
  const changeFilters = (filters) => {
    setLoading(true);
    setFilters(filters);
  };
  const changeSorting = (sorting) => {
    setLoading(true);
    setSorting(sorting);
  };
  const changeCurrentPage = (currentPage) => {
    setLoading(true);
    setCurrentPage(currentPage);
  };
  const changePageSize = (pageSize) => setPageSize(pageSize);

  /********************************************************/
  /*********************** Table data *********************/
  /********************************************************/
  const getDiscounts = async () => {
    if (columnsTranslations.length < 1) {
      return null;
    }

    let queryResult;

    const query = new Parse.Query("Discounts")
      .select(
        "allClients",
        "includedClients",
        "includedGroups",
        "includedTradeNames",
        "excludedClients",
        "excludedGroups",
        "excludedTradeNames",
        "startDate",
        "endDate",
        "percentageDiscount",
        "createdAt",
        "creator",
        "updatedAt",
        "lastUpdater",
        "status",
      )
      .include("includedClients")
      .include("includedGroups")
      .include("includedTradeNames")
      .include("excludedClients")
      .include("excludedGroups")
      .include("excludedTradeNames")
      .include("creator")
      .include("lastUpdater")
      .equalTo("type", "preSeason");
    filtersQuery(query, columnsTranslations, { columns, filters, sorting });

    // filtering special columns
    const includedClientsFilter = filters.find(
      (filter) => filter.columnName === "includedClients",
    );
    const includedGroupsFilter = filters.find(
      (filter) => filter.columnName === "includedGroups",
    );
    const includedTradeNamesFilter = filters.find(
      (filter) => filter.columnName === "includedTradeNames",
    );
    const excludedClientsFilter = filters.find(
      (filter) => filter.columnName === "excludedClients",
    );
    const excludedGroupsFilter = filters.find(
      (filter) => filter.columnName === "excludedGroups",
    );
    const excludedTradeNamesFilter = filters.find(
      (filter) => filter.columnName === "excludedTradeNames",
    );

    if (
      includedClientsFilter !== undefined ||
      includedGroupsFilter !== undefined ||
      includedTradeNamesFilter !== undefined ||
      excludedClientsFilter !== undefined ||
      excludedGroupsFilter !== undefined ||
      excludedTradeNamesFilter !== undefined
    ) {
      if (includedClientsFilter !== undefined) {
        const includedClients = new Parse.Query("Clients");
        includedClients.matches("CLCTNOM", includedClientsFilter.value, "i");
        query.matchesQuery("includedClients", includedClients);
      }
      if (includedGroupsFilter !== undefined) {
        const includedGroups = new Parse.Query("Groups").matches(
          "CLCTNOM",
          includedGroupsFilter.value,
          "i",
        );
        query.matchesQuery("includedGroups", includedGroups);
      }
      if (includedTradeNamesFilter !== undefined) {
        const includedTradeNames = await new Parse.Query("Tradenames").matches(
          "name",
          includedTradeNamesFilter.value,
          "i",
        );
        query.matchesQuery("includedTradeNames", includedTradeNames);
      }
      if (excludedClientsFilter !== undefined) {
        const excludedClients = new Parse.Query("Clients");
        excludedClients.matches("CLCTNOM", excludedClientsFilter.value, "i");
        query.matchesQuery("excludedClients", excludedClients);
      }
      if (excludedGroupsFilter !== undefined) {
        const excludedGroups = new Parse.Query("Groups").matches(
          "CLCTNOM",
          excludedGroupsFilter.value,
          "i",
        );
        query.matchesQuery("excludedGroups", excludedGroups);
      }
      if (excludedTradeNamesFilter !== undefined) {
        const excludedTradeNames = await new Parse.Query("Tradenames").matches(
          "name",
          excludedTradeNamesFilter.value,
          "i",
        );
        query.matchesQuery("excludedTradeNames", excludedTradeNames);
      }
    }

    const totalCount = await query.count();
    // skipping according to currentPage and pageSize
    queryResult = await query
      .skip(pageSize * currentPage)
      .limit(pageSize)
      .find();

    const discounts = [];

    // formatting data
    if (Array.isArray(queryResult)) {
      queryResult.forEach((discount) => {
        let data = { ...discount.attributes }; // shallow copy to complete object
        data.id = discount.id;

        // from array of pointers to array of strings
        if (discount.get("includedClients") !== undefined) {
          const includedClients = discount
            .get("includedClients")
            .map((includedClient) => includedClient.get("CLKTCODE"));
          data.includedClients = translateColumn(
            includedClients,
            columnsTranslations.find(
              (columnTranslation) =>
                columnTranslation.columnName === "includedClients",
            ),
          );
        }

        if (discount.get("includedGroups") !== undefined) {
          const includedGroups = discount
            .get("includedGroups")
            .map((includedGroup) => includedGroup.get("CLKTCODE"));
          data.includedGroups = translateColumn(
            includedGroups,
            columnsTranslations.find(
              (columnTranslation) =>
                columnTranslation.columnName === "includedGroups",
            ),
          );
        }

        if (discount.get("includedTradeNames") !== undefined) {
          const includedTradeNames = discount
            .get("includedTradeNames")
            .map((includedTradeName) => includedTradeName.get("CLCTGROUPE"));
          data.includedTradeNames = translateColumn(
            includedTradeNames,
            columnsTranslations.find(
              (columnTranslation) =>
                columnTranslation.columnName === "includedTradeNames",
            ),
          );
        }

        if (discount.get("excludedClients") !== undefined) {
          const excludedClients = discount
            .get("excludedClients")
            .map((excludedClient) => excludedClient.get("CLKTCODE"));
          data.excludedClients = translateColumn(
            excludedClients,
            columnsTranslations.find(
              (columnTranslation) =>
                columnTranslation.columnName === "excludedClients",
            ),
          );
        }

        if (discount.get("excludedGroups") !== undefined) {
          const excludedGroups = discount
            .get("excludedGroups")
            .map((excludedGroup) => excludedGroup.get("CLKTCODE"));
          data.excludedGroups = translateColumn(
            excludedGroups,
            columnsTranslations.find(
              (columnTranslation) =>
                columnTranslation.columnName === "excludedGroups",
            ),
          );
        }

        if (discount.get("excludedTradeNames") !== undefined) {
          const excludedTradeNames = discount
            .get("excludedTradeNames")
            .map((excludedTradeName) => excludedTradeName.get("CLCTGROUPE"));
          console.log("excludedTradeNames: ", excludedTradeNames);
          data.excludedTradeNames = translateColumn(
            excludedTradeNames,
            columnsTranslations.find(
              (columnTranslation) =>
                columnTranslation.columnName === "excludedTradeNames",
            ),
          );
        }

        data.allClients = data.allClients ? "oui" : "non";
        // formatting dates
        data.updatedAt = moment(data.updatedAt).format("DD/MM/YYYY HH:mm");
        data.createdAt = moment(data.createdAt).format("DD/MM/YYYY HH:mm");
        data.startDate = moment(data.startDate).format("DD/MM/YYYY");
        data.endDate = moment(data.endDate).format("DD/MM/YYYY");

        // formatting lastUpdater name
        data.lastUpdater =
          data.lastUpdater.get("firstname").charAt(0) +
          ". " +
          data.lastUpdater.get("lastname");

        discounts.push(data);
      });
    }
    setDiscounts(discounts);
    setTotalCount(totalCount);
    setLoading(false);
  };

  const listToColumnTranslations = (arrayOfListNameAndColumnName) => {
    const newColumnTranslations = [];
    arrayOfListNameAndColumnName.forEach((listNameAndColumnName) => {
      let listOfOptions = [];
      // getting list
      listsOptions.forEach((list) => {
        if (list.listName === listNameAndColumnName.listName) {
          listOfOptions = list.options;
        }
      });
      newColumnTranslations.push({
        columnName: listNameAndColumnName.columnName,
        values: listOfOptions.map((option) => [option.value, option.text]),
      });
    });
    setColumnsTranslations([...columnsTranslations, ...newColumnTranslations]);
  };

  /********************************************************/
  /************************* Modal ************************/
  /********************************************************/
  const handleClickOpen = (data, operation) => {
    switch (operation) {
      case "add":
        setDialogTitle("Création d'une remise");
        setDialogText("");
        setDialogYesBtnText("ajouter");
        setDialogNoBtnText("annuler");
        break;
      case "modify":
        setDialogTitle("Modification d'une remise");
        setDialogText("");
        setDialogYesBtnText("modifier");
        setDialogNoBtnText("annuler");
        setDiscountId(data);
        break;
      case "delete":
        setDialogTitle("Suppression d'une remise");
        setDialogText(
          'Voulez-vous vraiment supprimer la remise \'" + data.row.name + "\' (" + data.row.description + ") ?',
        );
        setDialogYesBtnText("oui");
        setDialogNoBtnText("non");
        setDiscountId(data.row.id);
        break;
      default:
    }
    setDialogOpen(true);
    setDialogOperation(operation);
  };

  // when closing dialog box
  const handleModalClose = async (action) => {
    switch (action) {
      case "discountAdded":
        handleSnackBarShow("remise ajoutée");
        break;
      case "discountUpdated":
        handleSnackBarShow("remise mise à jour");
        break;
      case "discountDeleted":
        handleSnackBarShow("remise supprimée");
        break;
      default:
    }
    setDialogOpen(false);
  };

  const handleModalValidate = async (discountData, operation) => {
    switch (operation) {
      case "delete":
        const discount = await new Parse.Query("Discounts").get(discountId);
        if (discount) {
          try {
            await discount.destroy();
          } catch (error) {
            console.log("Error: " + error.code + " " + error.message);
          }
        }
        await handleModalClose("discountDeleted");
        break;
      default:
        // for add and modify
        document
          .getElementById("editForm")
          .dispatchEvent(new Event("submit", { cancelable: true }));
        break;
    }
  };

  /********************************************************/
  /************************ actions ***********************/
  /********************************************************/
  const actions = [
    {
      icon: <EditIcon />,
      action: (rowData) => handleClickOpen(rowData.row.id, "modify"),
    },
    {
      icon: <DeleteIcon />,
      action: (rowData) => handleClickOpen(rowData, "delete"),
    },
  ];

  /********************************************************/
  /************************ SnackBar **********************/
  /********************************************************/
  const handleSnackBarShow = (textValue) => {
    setSnackBarOpen(true);
    setSnackBarText(textValue);
  };
  const handleSnackBarClose = () => {
    setSnackBarOpen(false);
  };

  /********************************************************/
  /************************ Stepper ***********************/
  /********************************************************/
  const getSteps = () => {
    return ["Nom et type", "Périmètre", "Période", "Conditions et remise"];
  };
  const getOptionalSteps = () => {
    return [];
  };
  const getStepContent = (activeStep, handleBack, handleNext, handleSkip) => {
    return (
      <HandleDiscountForm
        steps={getSteps()}
        activeStep={activeStep}
        optionalSteps={getOptionalSteps()}
        operation={dialogOperation}
        discountId={discountId}
        onHandleModalClose={handleModalClose}
        onChosenDiscountType={(dialogTitle) => setDialogTitle(dialogTitle)}
        onHandleBack={handleBack}
        onHandleNext={handleNext}
        onHandleSkip={handleSkip}
        currentUser={currentUser}
      />
    );
  };

  useEffect(() => {
    listToColumnTranslations([
      { listName: "clients", columnName: "includedclients" },
      { listName: "groups", columnName: "includedGroups" },
      { listName: "tradeNames", columnName: "includedTradeNames" },
      { listName: "clients", columnName: "excludedclients" },
      { listName: "groups", columnName: "excludedGroups" },
      { listName: "tradenames", columnName: "excludedTradeNames" },
    ]);
    setNumberColumns(columns, setStateNumberColumns);
    // eslint-disable-next-line
  }, []);

  // when page or sorting change or dialog is closed
  useEffect(() => {
    getDiscounts();
    // eslint-disable-next-line
  }, [currentPage, sorting, pageSize, dialogOpen, columnsTranslations]);

  // when filters change
  useEffect(() => {
    if (debouncedFilters) {
      getDiscounts();
    }
    // eslint-disable-next-line
  }, [debouncedFilters]);

  if (discounts === undefined) {
    return null;
  }

  const tableHasData = discounts !== undefined && discounts.length > 0;

  return (
    <>
      {!tableHasData && !editable && (
        <Typography variant="body1" className={classes.noDiscount}>
          Aucune remise n'a été trouvée
        </Typography>
      )}
      {/* if table is empty we only display it in Discount section because it's the only way to add discounts! */}
      {(tableHasData || editable) && (
        <Paper>
          <DataGridView
            title="Remises de présaison"
            withAddButton={addable}
            onAddButtonClick={() => handleClickOpen(null, "add")}
            rowsValues={discounts}
            columnHeaders={columns}
            currentPage={currentPage}
            onCurrentPageChange={changeCurrentPage}
            pageSize={pageSize}
            pageSizes={pageSizes}
            onPageSizeChange={changePageSize}
            filters={filters}
            onFiltersChange={changeFilters}
            sorting={sorting}
            onSortingChange={changeSorting}
            totalCount={totalCount}
            loading={loading}
            numberColumns={numberColumns}
            rowsPerPage="Nb de remises par page:"
            contentInfoSupplier={contentInfo}
            columnExtensions={columnExtensions}
            withPagination={true}
            hasActions={tableHasData && editable}
            actions={actions}
            noDataText="pas de remise de présaison"
          />
        </Paper>
      )}

      <Dialog
        fullScreen={dialogOperation !== "delete"}
        open={dialogOpen}
        onClose={handleModalClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        {dialogOperation !== "delete" && (
          <AppBar>
            <Toolbar className={classes.toolbar}>
              <IconButton
                color="inherit"
                onClick={handleModalClose}
                aria-label="Close"
              >
                <CloseIcon />
              </IconButton>
              <Typography variant="h6" color="inherit" className={classes.flex}>
                {dialogTitle}
              </Typography>
            </Toolbar>
          </AppBar>
        )}

        {dialogOperation === "delete" && (
          <DialogTitle id="alert-dialog-title">{dialogTitle}</DialogTitle>
        )}

        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {dialogText}
          </DialogContentText>
          {(dialogOperation === "add" || dialogOperation === "modify") && (
            <HorizontalLinearStepper
              onGetSteps={getSteps()}
              onGetStepContent={getStepContent}
              optionalSteps={getOptionalSteps()}
              navigationIsExternal
              dialogOperation={dialogOperation}
              onHandleModalValidate={handleModalValidate}
              onHandleModalClose={handleModalClose}
            />
          )}
        </DialogContent>

        {dialogOperation === "delete" && (
          <DialogActions>
            <Button
              onClick={() => handleModalValidate("", "delete")}
              color="primary"
            >
              {dialogYesBtnText}
            </Button>
            <Button
              onClick={() => handleModalClose()}
              color="primary"
              autoFocus
            >
              {dialogNoBtnText}
            </Button>
          </DialogActions>
        )}
      </Dialog>

      <Snackbar
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        autoHideDuration={3000}
        open={snackBarOpen}
        onClose={handleSnackBarClose}
        ContentProps={{
          "aria-describedby": "message-id",
        }}
        message={<span id="message-id">{snackBarText}</span>}
      />
    </>
  );
};

export default ProductDiscountList;
